import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Scale, Loader2, CheckCircle2, XCircle } from "lucide-react";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | error | timeout
  const [kind, setKind] = useState("subscription");
  const [credits, setCredits] = useState(0);
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (polls.current >= MAX_POLLS) { setStatus("timeout"); return; }
      polls.current += 1;
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.payment_status === "paid") {
          await refreshUser();
          setKind(data.kind || "subscription");
          setCredits(data.credits || 0);
          setStatus("success");
          setTimeout(() => navigate(data.kind === "credits" ? "/assistant" : "/dashboard"), 1800);
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCredits = kind === "credits";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md" data-testid="payment-success">
        <Scale className="w-8 h-8 text-primary mx-auto mb-8" />
        {status === "checking" && (<><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-6" /><h1 className="font-serif text-3xl mb-2">Confirming your payment…</h1><p className="text-muted-foreground text-sm">One moment while we process your order.</p></>)}
        {status === "success" && (<><CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-6" /><h1 className="font-serif text-4xl mb-2">{isCredits ? "Credits added." : "You're in."}</h1><p className="text-muted-foreground">{isCredits ? `${credits} AI credits are now on your account — opening the assistant.` : "Membership active — taking you to your dashboard."}</p></>)}
        {(status === "error" || status === "timeout") && (<><XCircle className="w-12 h-12 text-destructive-foreground mx-auto mb-6" /><h1 className="font-serif text-3xl mb-2">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1><p className="text-muted-foreground mb-6">{status === "timeout" ? "Your payment is taking longer than expected. Refresh in a moment." : "We couldn't confirm your payment."}</p><Link to="/subscribe" className="text-primary hover:underline">Back to membership</Link></>)}
      </div>
    </div>
  );
}
